import { useCallback, useEffect, useRef, useState } from "react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { GALLERY } from "@/lib/gallery";

export default function GalleryPage() {
  const { openAudit } = useSite();
  const [index, setIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const stageRef = useRef(null);
  const touchStart = useRef(null);
  const railRef = useRef(null);
  const closeRef = useRef(null);
  const lastTrigger = useRef(null);
  const total = GALLERY.length;

  const go = useCallback((next) => setIndex((i) => (i + next + total) % total), [total]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [go]);

  useEffect(() => {
    if (lightbox) {
      closeRef.current?.focus();
      const esc = (e) => { if (e.key === "Escape") setLightbox(false); };
      window.addEventListener("keydown", esc);
      document.body.style.overflow = "hidden";
      return () => {
        window.removeEventListener("keydown", esc);
        document.body.style.overflow = "";
        lastTrigger.current?.focus();
      };
    }
  }, [lightbox]);

  useEffect(() => {
    const rail = railRef.current;
    const active = rail?.children[index];
    active?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
    const node = document.getElementById("gallery-jsonld");
    if (node) node.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "ImageGallery",
      name: "Our Work — real installation photos",
      numberOfItems: total,
      provider: { "@type": "Organization", name: "Solar Black Belt", telephone: "+1-310-881-5155" },
    });
  }, [index, total]);

  const slide = GALLERY[index];
  const neighbors = [index - 1, index, index + 1].map((i) => GALLERY[(i + total) % total]);
  const openLightbox = (i) => {
    lastTrigger.current = i != null ? document.querySelector(`[data-testid=gallery-item-${i}]`) : null;
    setIndex(i ?? index);
    setLightbox(true);
  };

  return (
    <>
      <Seo
        title="Our Work — 110 real installation photos | Solar Black Belt"
        description="A gallery of real completed work: rooftop and ground solar installations, battery storage, inverters and roofing across Florida. Browse all 110 photos from the field."
      />
      <section className="page-hero">
        <div className="wrap">
          <span className="eyebrow">OUR WORK · REAL PHOTOS, REAL CREWS</span>
          <h1 data-testid="gallery-heading">{total} photos from the field.</h1>
          <p className="lead">
            Real completed work from our installation crews — rooftop arrays, battery storage, inverters, roofing and the
            details in between. No stock images, no staged before/afters, no invented locations: just the work, photographed
            as delivered. Swipe, tap the arrows or use your keyboard. Click any photo to view fullscreen.
          </p>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 36 }}>
        <div className="wrap">
          <div className="gallery-stage" data-testid="gallery-stage"
            onTouchStart={(e) => { touchStart.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              if (touchStart.current == null) return;
              const dx = e.changedTouches[0].clientX - touchStart.current;
              if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
              touchStart.current = null;
            }}>
            {neighbors.map((img) => (
              <img key={img.src} src={img.src} alt={img.alt} loading="lazy" style={{ display: "none" }} />
            ))}
            <img
              src={slide.src}
              alt={slide.alt}
              data-testid="gallery-active"
              className="gallery-main"
              style={{ aspectRatio: `${slide.w} / ${slide.h}`, maxWidth: `min(100%, ${Math.round((900 * slide.w) / slide.h)}px)` }}
              onClick={() => openLightbox(index)}
            />
            <button className="gallery-arrow left" aria-label="Previous photo" data-testid="gallery-prev" onClick={() => go(-1)}>←</button>
            <button className="gallery-arrow right" aria-label="Next photo" data-testid="gallery-next" onClick={() => go(1)}>→</button>
            <span className="gallery-count" data-testid="gallery-count">{index + 1} / {total}</span>
            <button className="gallery-count" style={{ cursor: "zoom-in" }} data-testid="gallery-expand" onClick={() => openLightbox(index)} aria-label="View fullscreen">⛶ Fullscreen</button>
          </div>

          <div className="gallery-rail" ref={railRef} data-testid="gallery-thumbs">
            {GALLERY.map((img, i) => (
              <button key={img.src} className={i === index ? "active" : ""} data-testid={`gallery-item-${i}`}
                aria-label={`Photo ${i + 1} of ${total}: ${img.alt}`} aria-current={i === index}
                onClick={() => { lastTrigger.current = null; setIndex(i); }}>
                <img src={img.thumb} alt="" loading="lazy" width={400} height={400} style={{ aspectRatio: `${img.w} / ${img.h}` }} />
              </button>
            ))}
          </div>
          <p className="micro">
            Photos are shown as supplied by the crews — captions, locations and system details are intentionally not invented.
            Want your building looked at next? <button className="linklike" onClick={() => openAudit()}>Upload your electric bill</button> or call 310-881-5155.
          </p>
        </div>
      </section>

      {lightbox && (
        <div className="lightbox" data-testid="gallery-lightbox" role="dialog" aria-modal="true" aria-label="Photo viewer"
          onClick={(e) => { if (e.target === e.currentTarget) setLightbox(false); }}>
          <img src={slide.src} alt={slide.alt} data-testid="gallery-lightbox-img" />
          <button ref={closeRef} className="gallery-arrow close" aria-label="Close photo viewer" data-testid="gallery-close" onClick={() => setLightbox(false)}>✕</button>
          <button className="gallery-arrow left" aria-label="Previous photo" onClick={() => go(-1)}>←</button>
          <button className="gallery-arrow right" aria-label="Next photo" onClick={() => go(1)}>→</button>
          <span className="gallery-count" data-testid="gallery-lightbox-count">{index + 1} / {total}</span>
        </div>
      )}
    </>
  );
}
